import { useState, useEffect, useCallback } from 'react';
import {
  Activity,
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  HelpCircle,
  PoundSterling,
  Server,
  Globe,
  ExternalLink,
  Clock,
} from 'lucide-react';
import { getAccessToken } from '@lib/msal';

// ── Types ─────────────────────────────────────────────────────────────────────

type CostPeriod = 'month-to-date' | 'year-to-date' | 'last-3-months' | 'last-6-months' | 'since-start';

const COST_PERIODS: { value: CostPeriod; label: string }[] = [
  { value: 'month-to-date', label: 'Month to Date' },
  { value: 'year-to-date', label: 'Year to Date' },
  { value: 'last-3-months', label: 'Last 3 Months' },
  { value: 'last-6-months', label: 'Last 6 Months' },
  { value: 'since-start', label: 'Since Subscription Start' },
];

interface CostService {
  name: string;
  cost: number;
}

interface Costs {
  services: CostService[];
  total: number;
  currency: string;
  fromDate: string;
  toDate: string;
  subscriptionStartDate: string | null;
}

interface ResourceHealth {
  name: string;
  resourceType: string;
  status: string;
  summary: string;
}

interface WebsiteStatus {
  name: string;
  url: string;
  status: 'up' | 'down' | 'degraded' | 'unknown';
  uptimePercent: number;
  responseTimeMs: number | null;
}

interface SystemStatusData {
  costs: Costs | null;
  costsError: string | null;
  costsPeriod: CostPeriod;
  resourceHealth: ResourceHealth[];
  resourceHealthError: string | null;
  websites: WebsiteStatus[];
  websitesError: string | null;
  websitesConfigured: boolean;
  fetchedAt: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(amount);
}

function formatTime(iso: string) {
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date(iso));
}

function formatDate(iso: string) {
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(iso));
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const s = status.toLowerCase();

  if (s === 'available' || s === 'up') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <CheckCircle2 className="h-3 w-3" />
        {s === 'available' ? 'Available' : 'Up'}
      </span>
    );
  }
  if (s === 'down' || s === 'unavailable') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
        <XCircle className="h-3 w-3" />
        {s === 'unavailable' ? 'Unavailable' : 'Down'}
      </span>
    );
  }
  if (s === 'degraded') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
        <AlertTriangle className="h-3 w-3" />
        Degraded
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
      <HelpCircle className="h-3 w-3" />
      Unknown
    </span>
  );
}

function SectionError({ message }: { message: string }) {
  return (
    <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
      <AlertTriangle className="h-4 w-4 flex-shrink-0" />
      {message}
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin h-8 w-8 border-4 border-sca-purple-500 border-t-transparent rounded-full" />
    </div>
  );
}

function resourceLabel(resourceType: string) {
  const map: Record<string, string> = {
    sites: 'App Service / Functions',
    serverfarms: 'App Service Plan',
    databaseaccounts: 'Cosmos DB',
    vaults: 'Key Vault',
    storageaccounts: 'Storage',
    workspaces: 'Log Analytics',
  };
  return map[resourceType.toLowerCase()] ?? resourceType;
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function SystemStatus() {
  const [data, setData] = useState<SystemStatusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<string | null>(null);
  const [costsPeriod, setCostsPeriod] = useState<CostPeriod>('month-to-date');

  const fetchStatus = useCallback(async (opts: { manual?: boolean; period?: CostPeriod } = {}) => {
    const { manual = false, period = costsPeriod } = opts;
    if (manual) setRefreshing(true);
    else setLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      const apiBase = import.meta.env.VITE_API_URL ?? '/api';
      const res = await fetch(`${apiBase}/system-status?costsPeriod=${period}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error(`Request failed: ${res.status}`);
      }

      const json = (await res.json()) as SystemStatusData;
      setData(json);
      setLastRefreshed(new Date().toISOString());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load status data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [costsPeriod]);

  // Initial load
  useEffect(() => {
    fetchStatus();
  }, []);

  // Re-fetch when period changes (skip initial render)
  const isFirstRender = useState(true);
  useEffect(() => {
    if (isFirstRender[0]) { isFirstRender[1](false); return; }
    fetchStatus({ period: costsPeriod });
  }, [costsPeriod]);

  // Auto-refresh every 60s
  useEffect(() => {
    const interval = setInterval(() => fetchStatus(), 60_000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-sca-purple-100 rounded-lg">
            <Activity className="h-5 w-5 text-sca-purple-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">System Status</h1>
            <p className="text-sm text-gray-500">Azure infrastructure, costs &amp; website health</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {lastRefreshed && (
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Updated {formatTime(lastRefreshed)}
            </span>
          )}
          <button
            onClick={() => fetchStatus({ manual: true })}
            disabled={refreshing || loading}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-sca-purple-600 bg-sca-purple-50 hover:bg-sca-purple-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Global error */}
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          <XCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {loading && <Spinner />}

      {!loading && data && (
        <>
          {/* ── Azure Costs ─────────────────────────────────────────── */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <PoundSterling className="h-4 w-4 text-sca-purple-500" />
                  <h2 className="text-sm font-semibold text-gray-900">Azure Costs</h2>
                </div>
                {/* Period selector */}
                <div className="flex items-center gap-1 flex-wrap">
                  {COST_PERIODS.map((p) => (
                    <button
                      key={p.value}
                      onClick={() => setCostsPeriod(p.value)}
                      className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                        costsPeriod === p.value
                          ? 'bg-sca-purple-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-5">
              {data.costsError ? (
                <SectionError message={data.costsError} />
              ) : data.costs && data.costs.services.length > 0 ? (
                <>
                  {/* Date range */}
                  <p className="text-xs text-gray-500 mb-4 flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {costsPeriod === 'since-start' && data.costs.subscriptionStartDate
                      ? <>Subscription started {formatDate(data.costs.subscriptionStartDate)} · showing {formatDate(data.costs.fromDate)} – {formatDate(data.costs.toDate)}</>
                      : <>{formatDate(data.costs.fromDate)} – {formatDate(data.costs.toDate)}</>
                    }
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100">
                          <th className="text-left pb-2">Service</th>
                          <th className="text-right pb-2">Cost</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {data.costs.services.map((s) => (
                          <tr key={s.name} className="font-mono">
                            <td className="py-2 text-gray-700 font-sans">{s.name}</td>
                            <td className="py-2 text-right text-gray-900 tabular-nums">
                              {formatCurrency(s.cost, data.costs!.currency)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-gray-200">
                          <td className="pt-3 text-sm font-semibold text-gray-900">Total</td>
                          <td className="pt-3 text-right text-sm font-bold text-sca-purple-600 tabular-nums font-mono">
                            {formatCurrency(data.costs.total, data.costs.currency)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  <p className="mt-3 text-xs text-gray-400">Pre-tax costs</p>
                </>
              ) : (
                <p className="text-sm text-gray-400">No cost data available for this period.</p>
              )}
            </div>
          </div>

          {/* ── Resource Health ──────────────────────────────────────── */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
              <Server className="h-4 w-4 text-sca-purple-500" />
              <h2 className="text-sm font-semibold text-gray-900">Azure Resource Health</h2>
            </div>

            <div className="p-5">
              {data.resourceHealthError ? (
                <SectionError message={data.resourceHealthError} />
              ) : data.resourceHealth.length > 0 ? (
                <div className="space-y-2">
                  {data.resourceHealth.map((r) => (
                    <div
                      key={r.name}
                      className="flex items-center justify-between py-2.5 px-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 font-mono truncate">{r.name}</p>
                        <p className="text-xs text-gray-400">{resourceLabel(r.resourceType)}</p>
                      </div>
                      <div className="flex-shrink-0 ml-4">
                        <StatusBadge status={r.status} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">No resource health data available.</p>
              )}
            </div>
          </div>

          {/* ── Website Status ───────────────────────────────────────── */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-sca-purple-500" />
                <h2 className="text-sm font-semibold text-gray-900">Website Uptime</h2>
              </div>
              {data.websitesConfigured && (
                <a
                  href="https://uptimerobot.com/dashboard"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-sca-purple-600 hover:underline flex items-center gap-1"
                >
                  UptimeRobot dashboard
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>

            <div className="p-5">
              {!data.websitesConfigured ? (
                <div className="text-center py-6">
                  <Globe className="h-8 w-8 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm font-medium text-gray-600 mb-1">UptimeRobot not configured</p>
                  <p className="text-xs text-gray-400 max-w-sm mx-auto">
                    Create a free account at{' '}
                    <a
                      href="https://uptimerobot.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sca-purple-600 hover:underline"
                    >
                      uptimerobot.com
                    </a>
                    , add your 3 monitors, then save your API key to Key Vault as{' '}
                    <code className="font-mono bg-gray-100 px-1 py-0.5 rounded text-xs">UptimeRobotApiKey</code>.
                  </p>
                </div>
              ) : data.websitesError ? (
                <SectionError message={data.websitesError} />
              ) : data.websites.length === 0 ? (
                <p className="text-sm text-gray-400">No monitors found. Add your sites in UptimeRobot.</p>
              ) : (
                <div className="space-y-3">
                  {data.websites.map((site) => (
                    <div
                      key={site.url}
                      className="flex items-center justify-between py-3 px-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-gray-900">{site.name}</p>
                          <a
                            href={site.url.startsWith('http') ? site.url : `https://${site.url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-sca-purple-500"
                          >
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                        <p className="text-xs text-gray-400 font-mono">{site.url}</p>
                      </div>
                      <div className="flex items-center gap-4 flex-shrink-0 ml-4">
                        {site.responseTimeMs !== null && (
                          <span className="text-xs text-gray-500 font-mono tabular-nums">
                            {site.responseTimeMs}ms
                          </span>
                        )}
                        {site.uptimePercent > 0 && (
                          <span className="text-xs text-gray-500 font-mono tabular-nums">
                            {site.uptimePercent.toFixed(2)}% uptime
                          </span>
                        )}
                        <StatusBadge status={site.status} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
